import { ArrowDown, ArrowUp, Download, Plus, Save, Trash2, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";

import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { FontsSettings } from "@/components/settings/fonts-settings";
import type { ModuleRow, PriceLibraryRow, TemplateRow } from "@/components/settings/settings-types";
import { TermsSettings } from "@/components/settings/terms-settings";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/client/api";
import { isTemplateSettingsPanel, templateSettingsPanels } from "@/lib/settings/navigation";
import { filterPriceLibraryItems, movePriceItemInModuleOrder } from "@/lib/settings/price-library-view";

type CatalogResponse = {
  templates: TemplateRow[];
  modules: ModuleRow[];
  priceLibrary: PriceLibraryRow[];
};

type ModuleForm = {
  code: string;
  titleEn: string;
  titleZh: string;
  description: string;
  sortOrder: number;
  active: boolean;
};

type TemplateForm = {
  code: string;
  nameEn: string;
  nameZh: string;
  projectType: string;
  description: string;
  defaultLanguageMode: "en_zh" | "en_only" | "zh_only";
  active: boolean;
  moduleCodes: string[];
};

type PriceForm = {
  moduleCode: string;
  nameEn: string;
  nameZh: string;
  descriptionEn: string;
  descriptionZh: string;
  defaultUnit: string;
  defaultRate: number;
  defaultTaxable: boolean;
  active: boolean;
  sortOrder: number;
};

const emptyModuleForm: ModuleForm = {
  code: "",
  titleEn: "",
  titleZh: "",
  description: "",
  sortOrder: 100,
  active: true,
};

const emptyTemplateForm: TemplateForm = {
  code: "",
  nameEn: "",
  nameZh: "",
  projectType: "",
  description: "",
  defaultLanguageMode: "en_zh",
  active: true,
  moduleCodes: [],
};

const emptyPriceForm: PriceForm = {
  moduleCode: "",
  nameEn: "",
  nameZh: "",
  descriptionEn: "",
  descriptionZh: "",
  defaultUnit: "Package",
  defaultRate: 0,
  defaultTaxable: true,
  active: true,
  sortOrder: 100,
};

export function CatalogSettings() {
  const { language } = useAppSettings();
  const text = language === "zh" ? zh : en;
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPanel = searchParams.get("panel");
  const activePanel = isTemplateSettingsPanel(requestedPanel) ? requestedPanel : "templates";
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [modules, setModules] = useState<ModuleRow[]>([]);
  const [priceLibrary, setPriceLibrary] = useState<PriceLibraryRow[]>([]);
  const [moduleDrafts, setModuleDrafts] = useState<Record<string, ModuleForm>>({});
  const [templateDrafts, setTemplateDrafts] = useState<Record<string, TemplateForm>>({});
  const [priceDrafts, setPriceDrafts] = useState<Record<string, PriceForm>>({});
  const [moduleForm, setModuleForm] = useState<ModuleForm>(emptyModuleForm);
  const [templateForm, setTemplateForm] = useState<TemplateForm>(emptyTemplateForm);
  const [priceForm, setPriceForm] = useState<PriceForm>(emptyPriceForm);
  const [priceModuleFilter, setPriceModuleFilter] = useState("all");
  const [priceSearch, setPriceSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [settingsRefreshToken, setSettingsRefreshToken] = useState(0);
  const importInputRef = useRef<HTMLInputElement>(null);
  const sortedModules = useMemo(() => [...modules].sort((left, right) => left.sortOrder - right.sortOrder), [modules]);
  const filteredPriceLibrary = useMemo(
    () => filterPriceLibraryItems(priceLibrary, { moduleCode: priceModuleFilter, query: priceSearch }),
    [priceLibrary, priceModuleFilter, priceSearch],
  );

  async function loadCatalog() {
    setLoading(true);
    setError(null);

    try {
      const response = await apiGet<CatalogResponse>("/api/settings/catalog");
      setTemplates(response.templates);
      setModules(response.modules);
      setPriceLibrary(response.priceLibrary);
      setModuleDrafts(
        Object.fromEntries(
          response.modules.map((module) => [
            module.code,
            {
              code: module.code,
              titleEn: module.titleEn,
              titleZh: module.titleZh,
              description: module.description ?? "",
              sortOrder: module.sortOrder,
              active: module.active,
            },
          ]),
        ),
      );
      setTemplateDrafts(
        Object.fromEntries(
          response.templates.map((template) => [
            template.id,
            {
              code: template.code,
              nameEn: template.nameEn,
              nameZh: template.nameZh,
              projectType: template.projectType,
              description: template.description ?? "",
              defaultLanguageMode: template.defaultLanguageMode,
              active: template.active,
              moduleCodes: template.moduleCodes,
            },
          ]),
        ),
      );
      setPriceDrafts(
        Object.fromEntries(
          response.priceLibrary.map((price) => [
            price.id,
            {
              moduleCode: price.moduleCode,
              nameEn: price.nameEn,
              nameZh: price.nameZh,
              descriptionEn: price.descriptionEn ?? "",
              descriptionZh: price.descriptionZh ?? "",
              defaultUnit: price.defaultUnit,
              defaultRate: minorToMajor(price.defaultRateMinor),
              defaultTaxable: price.defaultTaxable,
              active: price.active,
              sortOrder: price.sortOrder,
            },
          ]),
        ),
      );
      setPriceForm((current) => ({ ...current, moduleCode: current.moduleCode || response.modules[0]?.code || "" }));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : text.loadError);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  async function createModule(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSavingKey("module:new");
    setError(null);
    setMessage(null);

    try {
      await apiPost<ModulePayload, { code: string }>("/api/settings/modules", modulePayload(moduleForm));
      setModuleForm(emptyModuleForm);
      setMessage(text.moduleCreated);
      await loadCatalog();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : text.saveError);
    } finally {
      setSavingKey(null);
    }
  }

  async function saveModule(code: string) {
    const draft = moduleDrafts[code];

    if (!draft) return;

    setSavingKey(`module:${code}`);
    setError(null);
    setMessage(null);

    try {
      await apiPatch<Omit<ModulePayload, "code">, { ok: true }>(`/api/settings/modules/${code}`, modulePayload(draft));
      setMessage(text.moduleSaved);
      await loadCatalog();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : text.saveError);
    } finally {
      setSavingKey(null);
    }
  }

  async function deleteModule(code: string) {
    if (!window.confirm(text.confirmDeleteModule)) return;

    setSavingKey(`module:delete:${code}`);
    setError(null);
    setMessage(null);

    try {
      await apiDelete<{ ok: true }>(`/api/settings/modules/${code}`);
      setMessage(text.moduleDeleted);
      await loadCatalog();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : text.deleteError);
    } finally {
      setSavingKey(null);
    }
  }

  async function createTemplate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSavingKey("template:new");
    setError(null);
    setMessage(null);

    try {
      await apiPost<TemplatePayload, { id: string }>("/api/settings/templates", templatePayload(templateForm));
      setTemplateForm(emptyTemplateForm);
      setMessage(text.templateCreated);
      await loadCatalog();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : text.saveError);
    } finally {
      setSavingKey(null);
    }
  }

  async function saveTemplate(id: string) {
    const draft = templateDrafts[id];

    if (!draft) return;

    setSavingKey(`template:${id}`);
    setError(null);
    setMessage(null);

    try {
      await apiPatch<Omit<TemplatePayload, "code">, { ok: true }>(`/api/settings/templates/${id}`, templatePayload(draft));
      setMessage(text.templateSaved);
      await loadCatalog();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : text.saveError);
    } finally {
      setSavingKey(null);
    }
  }

  async function deleteTemplate(id: string) {
    if (!window.confirm(text.confirmDeleteTemplate)) return;

    setSavingKey(`template:delete:${id}`);
    setError(null);
    setMessage(null);

    try {
      await apiDelete<{ ok: true }>(`/api/settings/templates/${id}`);
      setMessage(text.templateDeleted);
      await loadCatalog();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : text.deleteError);
    } finally {
      setSavingKey(null);
    }
  }

  async function createPrice(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSavingKey("price:new");
    setError(null);
    setMessage(null);

    try {
      await apiPost<PricePayload, { id: string }>("/api/settings/price-library", pricePayload(priceForm));
      setPriceForm((current) => ({ ...emptyPriceForm, moduleCode: current.moduleCode }));
      setMessage(text.priceCreated);
      await loadCatalog();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : text.saveError);
    } finally {
      setSavingKey(null);
    }
  }

  async function savePrice(id: string) {
    const draft = priceDrafts[id];

    if (!draft) return;

    setSavingKey(`price:${id}`);
    setError(null);
    setMessage(null);

    try {
      await apiPatch<PricePayload, { ok: true }>(`/api/settings/price-library/${id}`, pricePayload(draft));
      setMessage(text.priceSaved);
      await loadCatalog();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : text.saveError);
    } finally {
      setSavingKey(null);
    }
  }

  async function deletePrice(id: string) {
    if (!window.confirm(text.confirmDeletePrice)) return;

    setSavingKey(`price:delete:${id}`);
    setError(null);
    setMessage(null);

    try {
      await apiDelete<{ ok: true }>(`/api/settings/price-library/${id}`);
      setMessage(text.priceDeleted);
      await loadCatalog();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : text.deleteError);
    } finally {
      setSavingKey(null);
    }
  }

  async function exportSettingsSnapshot() {
    setSavingKey("catalog:export");
    setError(null);
    setMessage(null);

    try {
      const response = await fetch("/api/settings/catalog-snapshot", { credentials: "include" });

      if (!response.ok) {
        throw new Error(await readApiError(response, text.exportError));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = getDownloadFileName(response.headers.get("content-disposition"));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage(text.exported);
    } catch (exportError) {
      setError(exportError instanceof Error ? exportError.message : text.exportError);
    } finally {
      setSavingKey(null);
    }
  }

  async function movePrice(id: string, direction: "up" | "down") {
    const price = priceLibrary.find((item) => item.id === id);
    if (!price) return;

    const patches = movePriceItemInModuleOrder(priceLibrary, id, direction);
    if (patches.length === 0) return;

    setSavingKey(`price-order:${price.moduleCode}`);
    setError(null);
    setMessage(null);

    try {
      await Promise.all(
        patches.map((patch) =>
          apiPatch<Partial<PricePayload>, { ok: true }>(`/api/settings/price-library/${patch.id}`, { sortOrder: patch.sortOrder }),
        ),
      );
      setMessage(text.priceOrderSaved);
      await loadCatalog();
    } catch (moveError) {
      setError(moveError instanceof Error ? moveError.message : text.priceOrderError);
    } finally {
      setSavingKey(null);
    }
  }

  async function importSettingsSnapshot(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    if (!file) return;
    if (!window.confirm(text.confirmImport)) {
      event.target.value = "";
      return;
    }

    setSavingKey("catalog:import");
    setError(null);
    setMessage(null);

    try {
      let payload: unknown;

      try {
        payload = JSON.parse(await file.text()) as unknown;
      } catch {
        throw new Error(text.invalidImportFile);
      }

      const response = await fetch("/api/settings/catalog-snapshot", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await readApiError(response, text.importError));
      }

      setMessage(text.imported);
      setSettingsRefreshToken((current) => current + 1);
      await loadCatalog();
    } catch (importError) {
      setError(importError instanceof Error ? importError.message : text.importError);
    } finally {
      setSavingKey(null);
      event.target.value = "";
    }
  }

  return (
    <section className="space-y-4">
      <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{text.title}</h1>
          <p className="text-sm text-muted-foreground">{text.description}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={() => void exportSettingsSnapshot()} disabled={savingKey === "catalog:export" || savingKey === "catalog:import"}>
            <Download className="h-4 w-4" aria-hidden="true" />
            {savingKey === "catalog:export" ? text.exporting : text.exportSettings}
          </Button>
          <Button type="button" variant="secondary" onClick={() => importInputRef.current?.click()} disabled={savingKey === "catalog:export" || savingKey === "catalog:import"}>
            <Upload className="h-4 w-4" aria-hidden="true" />
            {savingKey === "catalog:import" ? text.importing : text.importSettings}
          </Button>
          <input ref={importInputRef} type="file" accept="application/json,.json" className="hidden" onChange={(event) => void importSettingsSnapshot(event)} />
        </div>
      </header>

      <StatusMessage message={message} error={error} />

      <div className="flex flex-wrap gap-2">
        {templateSettingsPanels.map((panel) => (
          <Button key={panel.id} type="button" variant={activePanel === panel.id ? "primary" : "secondary"} onClick={() => selectPanel(panel.id)}>
            {text[panel.id]}
          </Button>
        ))}
      </div>

      {isCatalogPanel(activePanel) && loading ? <Card className="p-4 text-sm text-muted-foreground">{text.loading}</Card> : null}
      {!loading && activePanel === "templates" ? renderTemplates() : null}
      {!loading && activePanel === "modules" ? renderModules() : null}
      {!loading && activePanel === "prices" ? renderPrices() : null}
      {activePanel === "fonts" ? <FontsSettings refreshToken={settingsRefreshToken} /> : null}
      {activePanel === "terms" ? <TermsSettings refreshToken={settingsRefreshToken} /> : null}
    </section>
  );

  function selectPanel(panel: typeof activePanel) {
    const nextParams = new URLSearchParams(searchParams);

    if (panel === "templates") {
      nextParams.delete("panel");
    } else {
      nextParams.set("panel", panel);
    }

    setSearchParams(nextParams, { replace: true });
  }

  function renderModules() {
    return (
      <div className="space-y-4">
        <Card className="p-4">
          <form className="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_100px_110px_auto]" onSubmit={createModule}>
            <Field label={text.code} value={moduleForm.code} onChange={(value) => setModuleForm({ ...moduleForm, code: value })} />
            <Field label={text.titleEn} value={moduleForm.titleEn} onChange={(value) => setModuleForm({ ...moduleForm, titleEn: value })} />
            <Field label={text.titleZh} value={moduleForm.titleZh} onChange={(value) => setModuleForm({ ...moduleForm, titleZh: value })} />
            <NumberField label={text.order} value={moduleForm.sortOrder} onChange={(value) => setModuleForm({ ...moduleForm, sortOrder: value })} />
            <ToggleField label={text.active} checked={moduleForm.active} onChange={(checked) => setModuleForm({ ...moduleForm, active: checked })} />
            <Button type="submit" className="mt-5" disabled={savingKey === "module:new"}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              {text.create}
            </Button>
          </form>
        </Card>
        <div className="space-y-3">
          {sortedModules.map((module) => {
            const draft = moduleDrafts[module.code];

            if (!draft) return null;

            return (
              <Card key={module.code} className="p-4">
                <div className="grid gap-3 lg:grid-cols-[1fr_1fr_90px_110px_auto_auto]">
                  <Field label={text.titleEn} value={draft.titleEn} onChange={(value) => updateModuleDraft(module.code, { titleEn: value })} />
                  <Field label={text.titleZh} value={draft.titleZh} onChange={(value) => updateModuleDraft(module.code, { titleZh: value })} />
                  <NumberField label={text.order} value={draft.sortOrder} onChange={(value) => updateModuleDraft(module.code, { sortOrder: value })} />
                  <ToggleField label={text.active} checked={draft.active} onChange={(checked) => updateModuleDraft(module.code, { active: checked })} />
                  <Button type="button" variant="secondary" className="mt-5" onClick={() => void saveModule(module.code)} disabled={savingKey === `module:${module.code}`}>
                    <Save className="h-4 w-4" aria-hidden="true" />
                    {text.save}
                  </Button>
                  <Button type="button" variant="ghost" className="mt-5 text-red-600 hover:text-red-700" onClick={() => void deleteModule(module.code)} disabled={savingKey === `module:delete:${module.code}`}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                    {text.delete}
                  </Button>
                </div>
                <label className="mt-3 block">
                  <span className="mb-1 block text-xs font-medium text-muted-foreground">{text.descriptionField}</span>
                  <Textarea value={draft.description} onChange={(event) => updateModuleDraft(module.code, { description: event.target.value })} />
                </label>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  function renderTemplates() {
    return (
      <div className="space-y-4">
        <Card className="p-4">
          <form className="space-y-3" onSubmit={createTemplate}>
            <div className="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_1fr_150px_110px_auto]">
              <Field label={text.code} value={templateForm.code} onChange={(value) => setTemplateForm({ ...templateForm, code: value })} />
              <Field label={text.nameEn} value={templateForm.nameEn} onChange={(value) => setTemplateForm({ ...templateForm, nameEn: value })} />
              <Field label={text.nameZh} value={templateForm.nameZh} onChange={(value) => setTemplateForm({ ...templateForm, nameZh: value })} />
              <Field label={text.projectType} value={templateForm.projectType} onChange={(value) => setTemplateForm({ ...templateForm, projectType: value })} />
              <LanguageField value={templateForm.defaultLanguageMode} onChange={(value) => setTemplateForm({ ...templateForm, defaultLanguageMode: value })} text={text} />
              <ToggleField label={text.active} checked={templateForm.active} onChange={(checked) => setTemplateForm({ ...templateForm, active: checked })} />
              <Button type="submit" className="mt-5" disabled={savingKey === "template:new"}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                {text.create}
              </Button>
            </div>
            <ModuleChecklist modules={sortedModules} selected={templateForm.moduleCodes} label={text.modulesInTemplate} language={language} onChange={(moduleCodes) => setTemplateForm({ ...templateForm, moduleCodes })} />
          </form>
        </Card>
        <div className="space-y-3">
          {templates.map((template) => {
            const draft = templateDrafts[template.id];

            if (!draft) return null;

            return (
              <Card key={template.id} className="p-4">
                <div className="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_150px_110px_auto_auto]">
                  <Field label={text.nameEn} value={draft.nameEn} onChange={(value) => updateTemplateDraft(template.id, { nameEn: value })} />
                  <Field label={text.nameZh} value={draft.nameZh} onChange={(value) => updateTemplateDraft(template.id, { nameZh: value })} />
                  <Field label={text.projectType} value={draft.projectType} onChange={(value) => updateTemplateDraft(template.id, { projectType: value })} />
                  <LanguageField value={draft.defaultLanguageMode} onChange={(value) => updateTemplateDraft(template.id, { defaultLanguageMode: value })} text={text} />
                  <ToggleField label={text.active} checked={draft.active} onChange={(checked) => updateTemplateDraft(template.id, { active: checked })} />
                  <Button type="button" variant="secondary" className="mt-5" onClick={() => void saveTemplate(template.id)} disabled={savingKey === `template:${template.id}`}>
                    <Save className="h-4 w-4" aria-hidden="true" />
                    {text.save}
                  </Button>
                  <Button type="button" variant="ghost" className="mt-5 text-red-600 hover:text-red-700" onClick={() => void deleteTemplate(template.id)} disabled={savingKey === `template:delete:${template.id}`}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                    {text.delete}
                  </Button>
                </div>
                <label className="mt-3 block">
                  <span className="mb-1 block text-xs font-medium text-muted-foreground">{text.descriptionField}</span>
                  <Textarea value={draft.description} onChange={(event) => updateTemplateDraft(template.id, { description: event.target.value })} />
                </label>
                <ModuleChecklist modules={sortedModules} selected={draft.moduleCodes} label={text.modulesInTemplate} language={language} onChange={(moduleCodes) => updateTemplateDraft(template.id, { moduleCodes })} />
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  function renderPrices() {
    return (
      <div className="space-y-4">
        <Card className="p-4">
          <div className="grid gap-3 lg:grid-cols-[minmax(220px,1fr)_minmax(260px,2fr)] lg:items-end">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">{text.filterModule}</span>
              <select className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm" value={priceModuleFilter} onChange={(event) => setPriceModuleFilter(event.target.value)}>
                <option value="all">{text.allModules}</option>
                {sortedModules.map((module) => (
                  <option key={module.code} value={module.code}>
                    {language === "zh" ? module.titleZh : module.titleEn}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">{text.searchPrices}</span>
              <Input value={priceSearch} onChange={(event) => setPriceSearch(event.target.value)} placeholder={text.searchPricesPlaceholder} />
            </label>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{text.priceFilterSummary.replace("{count}", String(filteredPriceLibrary.length)).replace("{total}", String(priceLibrary.length))}</p>
        </Card>
        <Card className="p-4">
          <form className="space-y-3" onSubmit={createPrice}>
            <div className="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_100px_120px_110px_auto]">
              <ModuleSelect label={text.module} modules={sortedModules} language={language} value={priceForm.moduleCode} onChange={(moduleCode) => setPriceForm({ ...priceForm, moduleCode })} />
              <Field label={text.nameEn} value={priceForm.nameEn} onChange={(value) => setPriceForm({ ...priceForm, nameEn: value })} />
              <Field label={text.nameZh} value={priceForm.nameZh} onChange={(value) => setPriceForm({ ...priceForm, nameZh: value })} />
              <Field label={text.unit} value={priceForm.defaultUnit} onChange={(value) => setPriceForm({ ...priceForm, defaultUnit: value })} />
              <NumberField label={text.rate} value={priceForm.defaultRate} onChange={(value) => setPriceForm({ ...priceForm, defaultRate: value })} />
              <ToggleField label={text.taxable} checked={priceForm.defaultTaxable} onChange={(checked) => setPriceForm({ ...priceForm, defaultTaxable: checked })} />
              <Button type="submit" className="mt-5" disabled={savingKey === "price:new"}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                {text.create}
              </Button>
            </div>
          </form>
        </Card>
        <div className="space-y-3">
          {filteredPriceLibrary.length === 0 ? <Card className="p-4 text-sm text-muted-foreground">{text.noPriceItems}</Card> : null}
          {filteredPriceLibrary.map((price) => {
            const draft = priceDrafts[price.id];

            if (!draft) return null;
            const movingPrice = savingKey === `price-order:${price.moduleCode}`;
            const canMoveUp = movePriceItemInModuleOrder(priceLibrary, price.id, "up").length > 0;
            const canMoveDown = movePriceItemInModuleOrder(priceLibrary, price.id, "down").length > 0;

            return (
              <Card key={price.id} className="p-4">
                <div className="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_90px_110px_100px_100px_auto]">
                  <ModuleSelect label={text.module} modules={sortedModules} language={language} value={draft.moduleCode} onChange={(moduleCode) => updatePriceDraft(price.id, { moduleCode })} />
                  <Field label={text.nameEn} value={draft.nameEn} onChange={(value) => updatePriceDraft(price.id, { nameEn: value })} />
                  <Field label={text.nameZh} value={draft.nameZh} onChange={(value) => updatePriceDraft(price.id, { nameZh: value })} />
                  <Field label={text.unit} value={draft.defaultUnit} onChange={(value) => updatePriceDraft(price.id, { defaultUnit: value })} />
                  <NumberField label={text.rate} value={draft.defaultRate} onChange={(value) => updatePriceDraft(price.id, { defaultRate: value })} />
                  <ToggleField label={text.taxable} checked={draft.defaultTaxable} onChange={(checked) => updatePriceDraft(price.id, { defaultTaxable: checked })} />
                  <ToggleField label={text.active} checked={draft.active} onChange={(checked) => updatePriceDraft(price.id, { active: checked })} />
                  <div className="mt-5 flex flex-wrap gap-1">
                    <Button type="button" variant="secondary" className="h-9 w-9 px-0" aria-label={text.movePriceUp} title={text.movePriceUp} onClick={() => void movePrice(price.id, "up")} disabled={!canMoveUp || movingPrice}>
                      <ArrowUp className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    <Button type="button" variant="secondary" className="h-9 w-9 px-0" aria-label={text.movePriceDown} title={text.movePriceDown} onClick={() => void movePrice(price.id, "down")} disabled={!canMoveDown || movingPrice}>
                      <ArrowDown className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    <Button type="button" variant="secondary" className="h-9 px-2" onClick={() => void savePrice(price.id)} disabled={savingKey === `price:${price.id}`}>
                      <Save className="h-4 w-4" aria-hidden="true" />
                      {text.save}
                    </Button>
                    <Button type="button" variant="ghost" className="h-9 px-2 text-red-600 hover:text-red-700" onClick={() => void deletePrice(price.id)} disabled={savingKey === `price:delete:${price.id}`}>
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                      {text.delete}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  function updateModuleDraft(code: string, patch: Partial<ModuleForm>) {
    setModuleDrafts((current) => ({ ...current, [code]: { ...(current[code] ?? emptyModuleForm), ...patch } }));
  }

  function updateTemplateDraft(id: string, patch: Partial<TemplateForm>) {
    setTemplateDrafts((current) => ({ ...current, [id]: { ...(current[id] ?? emptyTemplateForm), ...patch } }));
  }

  function updatePriceDraft(id: string, patch: Partial<PriceForm>) {
    setPriceDrafts((current) => ({ ...current, [id]: { ...(current[id] ?? emptyPriceForm), ...patch } }));
  }
}

type ModulePayload = {
  code: string;
  titleEn: string;
  titleZh: string;
  description: string | null;
  sortOrder: number;
  active: boolean;
};

type TemplatePayload = {
  code: string;
  nameEn: string;
  nameZh: string;
  projectType: string;
  description: string | null;
  defaultLanguageMode: "en_zh" | "en_only" | "zh_only";
  active: boolean;
  moduleCodes: string[];
};

type PricePayload = {
  moduleCode: string;
  nameEn: string;
  nameZh: string;
  descriptionEn: string | null;
  descriptionZh: string | null;
  defaultUnit: string;
  defaultRateMinor: number;
  defaultTaxable: boolean;
  active: boolean;
  sortOrder: number;
};

function modulePayload(form: ModuleForm): ModulePayload {
  return {
    code: form.code.trim(),
    titleEn: form.titleEn.trim(),
    titleZh: form.titleZh.trim(),
    description: emptyToNull(form.description),
    sortOrder: Number(form.sortOrder),
    active: form.active,
  };
}

function templatePayload(form: TemplateForm): TemplatePayload {
  return {
    code: form.code.trim(),
    nameEn: form.nameEn.trim(),
    nameZh: form.nameZh.trim(),
    projectType: form.projectType.trim(),
    description: emptyToNull(form.description),
    defaultLanguageMode: form.defaultLanguageMode,
    active: form.active,
    moduleCodes: form.moduleCodes,
  };
}

function pricePayload(form: PriceForm): PricePayload {
  return {
    moduleCode: form.moduleCode,
    nameEn: form.nameEn.trim(),
    nameZh: form.nameZh.trim(),
    descriptionEn: emptyToNull(form.descriptionEn),
    descriptionZh: emptyToNull(form.descriptionZh),
    defaultUnit: form.defaultUnit.trim() || "Package",
    defaultRateMinor: majorToMinor(form.defaultRate),
    defaultTaxable: form.defaultTaxable,
    active: form.active,
    sortOrder: Number(form.sortOrder),
  };
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <Input value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <Input type="number" value={value} onChange={(event) => onChange(Number(event.target.value))} />
    </label>
  );
}

function ToggleField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="mt-5 flex h-9 items-center gap-2 rounded-md border border-border px-3 text-sm">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="h-4 w-4 accent-primary" />
      {label}
    </label>
  );
}

function LanguageField({
  text,
  value,
  onChange,
}: {
  text: typeof en;
  value: TemplateForm["defaultLanguageMode"];
  onChange: (value: TemplateForm["defaultLanguageMode"]) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{text.language}</span>
      <select className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm" value={value} onChange={(event) => onChange(event.target.value as TemplateForm["defaultLanguageMode"])}>
        <option value="en_zh">{text.bilingual}</option>
        <option value="en_only">{text.englishOnly}</option>
        <option value="zh_only">{text.chineseOnly}</option>
      </select>
    </label>
  );
}

function ModuleSelect({ label, modules, language, value, onChange }: { label: string; modules: ModuleRow[]; language: "en" | "zh"; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <select className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm" value={value} onChange={(event) => onChange(event.target.value)}>
        {modules.map((module) => (
          <option key={module.code} value={module.code}>
            {language === "zh" ? module.titleZh : module.titleEn}
          </option>
        ))}
      </select>
    </label>
  );
}

function ModuleChecklist({
  label,
  modules,
  selected,
  language,
  onChange,
}: {
  label: string;
  modules: ModuleRow[];
  selected: string[];
  language: "en" | "zh";
  onChange: (moduleCodes: string[]) => void;
}) {
  return (
    <div className="mt-3">
      <div className="mb-2 text-xs font-medium text-muted-foreground">{label}</div>
      <div className="grid max-h-48 gap-2 overflow-auto rounded-md border border-border p-2 md:grid-cols-2 xl:grid-cols-3">
        {modules.map((module) => {
          const checked = selected.includes(module.code);

          return (
            <label key={module.code} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => {
                  onChange(checked ? selected.filter((code) => code !== module.code) : [...selected, module.code]);
                }}
                className="h-4 w-4 accent-primary"
              />
              <span>{language === "zh" ? module.titleZh : module.titleEn}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function StatusMessage({ message, error }: { message: string | null; error: string | null }) {
  if (!message && !error) return null;

  return (
    <div className={error ? "rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" : "rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary"}>
      {error ?? message}
    </div>
  );
}

function isCatalogPanel(panel: string): panel is "templates" | "modules" | "prices" {
  return panel === "templates" || panel === "modules" || panel === "prices";
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function majorToMinor(value: number): number {
  return Math.round(Number(value || 0) * 100);
}

function minorToMajor(value: number): number {
  return Number((value / 100).toFixed(2));
}

async function readApiError(response: Response, fallback: string): Promise<string> {
  try {
    const payload = (await response.json()) as { error?: string };
    return payload.error ?? fallback;
  } catch {
    return fallback;
  }
}

function getDownloadFileName(contentDisposition: string | null): string {
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition ?? "");
  return match?.[1] ?? `vzen-settings-catalog-${new Date().toISOString().slice(0, 10)}.json`;
}

const en = {
  title: "Templates",
  description: "Manage quote templates, module library, template-module defaults, and reusable price items.",
  templates: "Templates",
  modules: "Modules",
  prices: "Price Library",
  fonts: "Fonts",
  terms: "Terms",
  code: "Code",
  titleEn: "English title",
  titleZh: "Chinese title",
  nameEn: "English name",
  nameZh: "Chinese name",
  projectType: "Project type",
  descriptionField: "Description",
  modulesInTemplate: "Default modules",
  language: "Language",
  bilingual: "Bilingual",
  englishOnly: "English",
  chineseOnly: "Chinese",
  module: "Module",
  unit: "Unit",
  rate: "Rate",
  taxable: "Taxable",
  active: "Active",
  order: "Order",
  create: "Create",
  save: "Save",
  delete: "Delete",
  exportSettings: "Export settings",
  importSettings: "Import settings",
  exporting: "Exporting...",
  importing: "Importing...",
  loading: "Loading catalog...",
  exported: "Settings backup exported.",
  imported: "Settings backup imported.",
  moduleCreated: "Module created.",
  moduleSaved: "Module saved.",
  moduleDeleted: "Module deleted.",
  templateCreated: "Template created.",
  templateSaved: "Template saved.",
  templateDeleted: "Template deleted.",
  priceCreated: "Price item created.",
  priceSaved: "Price item saved.",
  priceDeleted: "Price item deleted.",
  filterModule: "Filter module",
  allModules: "All modules",
  searchPrices: "Search price items",
  searchPricesPlaceholder: "Search English, Chinese, unit, or description",
  movePriceUp: "Move price item up",
  movePriceDown: "Move price item down",
  priceOrderSaved: "Price item order saved.",
  priceOrderError: "Unable to save price item order.",
  priceFilterSummary: "Showing {count} of {total} price items.",
  noPriceItems: "No price items match the current filters.",
  confirmDeleteModule: "Delete this module? Template links and price items under it will also be removed.",
  confirmDeleteTemplate: "Delete this template?",
  confirmDeletePrice: "Delete this price item?",
  confirmImport: "Importing this file will replace templates, modules, price library, fonts, and terms. Continue?",
  loadError: "Unable to load catalog.",
  saveError: "Unable to save catalog changes.",
  deleteError: "Unable to delete catalog item.",
  exportError: "Unable to export settings.",
  importError: "Unable to import settings. Check that the file is valid JSON exported from this page.",
  invalidImportFile: "The selected file is not valid JSON.",
};

const zh: typeof en = {
  title: "模板",
  description: "管理报价模板、模块库、模板默认模块和可复用价格项。",
  templates: "模板",
  modules: "模块",
  prices: "价格库",
  fonts: "字体",
  terms: "条款",
  code: "代码",
  titleEn: "英文标题",
  titleZh: "中文标题",
  nameEn: "英文名称",
  nameZh: "中文名称",
  projectType: "项目类型",
  descriptionField: "说明",
  modulesInTemplate: "默认模块",
  language: "语言",
  bilingual: "双语",
  englishOnly: "英文",
  chineseOnly: "中文",
  module: "模块",
  unit: "单位",
  rate: "单价",
  taxable: "计税",
  active: "启用",
  order: "排序",
  create: "创建",
  save: "保存",
  delete: "删除",
  exportSettings: "导出设置",
  importSettings: "导入设置",
  exporting: "正在导出...",
  importing: "正在导入...",
  loading: "正在加载目录...",
  exported: "设置备份已导出。",
  imported: "设置备份已导入。",
  moduleCreated: "模块已创建。",
  moduleSaved: "模块已保存。",
  moduleDeleted: "模块已删除。",
  templateCreated: "模板已创建。",
  templateSaved: "模板已保存。",
  templateDeleted: "模板已删除。",
  priceCreated: "价格项已创建。",
  priceSaved: "价格项已保存。",
  priceDeleted: "价格项已删除。",
  filterModule: "筛选模块",
  allModules: "全部模块",
  searchPrices: "搜索价格项",
  searchPricesPlaceholder: "搜索英文、中文、单位或说明",
  movePriceUp: "上移价格项",
  movePriceDown: "下移价格项",
  priceOrderSaved: "价格项顺序已保存。",
  priceOrderError: "无法保存价格项顺序。",
  priceFilterSummary: "当前显示 {count} / {total} 个价格项。",
  noPriceItems: "当前筛选条件下没有价格项。",
  confirmDeleteModule: "确认删除这个模块？它在模板中的关联和下属价格项也会被删除。",
  confirmDeleteTemplate: "确认删除这个模板？",
  confirmDeletePrice: "确认删除这个价格项？",
  confirmImport: "导入此文件会替换模板、模块、价格库、字体和条款。确认继续？",
  loadError: "无法加载目录。",
  saveError: "无法保存目录变更。",
  deleteError: "无法删除目录项。",
  exportError: "无法导出设置。",
  importError: "无法导入设置。请确认文件是从本页面导出的有效 JSON。",
  invalidImportFile: "选择的文件不是有效 JSON。",
};
